import { useEffect, useState } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { Users, Megaphone, Send, TrendingUp, CheckCircle, Clock, Shield, AlertTriangle, ShieldAlert, ShieldCheck, Filter, X, Save, RefreshCw, Calendar } from 'lucide-react';
import { SkeletonStat, SkeletonTableRows } from '../components/Skeletons';
import { useT } from '../i18n';
import ajuda from '../utils/ajuda';

const planoBadge = {
  freemium:   { bg: '#f0fdf4', color: '#15803d' },
  starter:    { bg: '#f1f2f7', color: 'var(--color-text-soft)' },
  pro:        { bg: 'var(--color-accent-soft)', color: 'var(--color-accent)' },
  enterprise: { bg: '#fdf4ff', color: '#13305f' },
  teste:      { bg: '#fff7ed', color: '#ea580c' },
};

function formatarTelefone(raw) {
  if (!raw) return '—';
  const d = String(raw).replace(/\D/g, '');
  if (d.length === 13 && d.startsWith('55')) return `+55 (${d.slice(2, 4)}) ${d.slice(4, 9)}-${d.slice(9)}`;
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return raw;
}

function exportarCSV(todosOsTenants) {
  const headers = ['Empresa', 'Email', 'Telefone', 'Plano', 'Período', 'Status', 'Campanhas', 'Contatos', 'Disparos', 'Cadastro', 'Último acesso', 'Vencimento'];
  const rows = todosOsTenants.map(tn => [
    tn.nome_empresa, tn.email_admin, tn.celular, tn.plano, tn.periodo_cobranca, tn.status,
    tn.campanhas, tn.contatos, tn.envios,
    tn.created_at ? new Date(tn.created_at).toLocaleDateString('pt-BR') : '',
    tn.ultimo_acesso ? new Date(tn.ultimo_acesso).toLocaleDateString('pt-BR') : '',
    tn.data_vencimento ? new Date(tn.data_vencimento).toLocaleDateString('pt-BR') : '',
  ]);
  const csv = [headers, ...rows].map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `clientes_riia_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function exportarCSVCliente(tn) {
  const rows = [
    ['Campo', 'Valor'],
    ['Empresa', tn.nome_empresa], ['Email', tn.email_admin], ['Telefone', tn.celular],
    ['Plano', tn.plano], ['Período', tn.periodo_cobranca], ['Status', tn.status],
    ['Campanhas', tn.campanhas], ['Contatos', tn.contatos], ['Disparos', tn.envios],
    ['Cadastro', tn.created_at ? new Date(tn.created_at).toLocaleDateString('pt-BR') : ''],
    ['Último acesso', tn.ultimo_acesso ? new Date(tn.ultimo_acesso).toLocaleDateString('pt-BR') : ''],
    ['Vencimento', tn.data_vencimento ? new Date(tn.data_vencimento).toLocaleDateString('pt-BR') : ''],
  ];
  const csv = rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `cliente_${String(tn.nome_empresa || 'sem-nome').replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// Último acesso: data + tempo relativo. Verde se ≤7d, cinza se mais antigo.
function formatarUltimoAcesso(dt) {
  if (!dt) return { texto: 'Nunca', cor: 'var(--color-text-muted)', relativo: '' };
  const data = new Date(dt);
  const agora = new Date();
  const diffMs = agora - data;
  const dias = Math.floor(diffMs / 86400000);
  const horas = Math.floor(diffMs / 3600000);
  const minutos = Math.floor(diffMs / 60000);

  let relativo;
  if (minutos < 1) relativo = 'agora';
  else if (minutos < 60) relativo = `há ${minutos}min`;
  else if (horas < 24) relativo = `há ${horas}h`;
  else if (dias === 1) relativo = 'ontem';
  else relativo = `há ${dias}d`;

  const cor = dias <= 7 ? 'var(--color-success)' : 'var(--color-text-muted)';
  return { texto: data.toLocaleDateString('pt-BR'), cor, relativo };
}

export default function Admin() {
  const { t } = useT();
  const [dashboard, setDashboard] = useState(null);
  const [dashboardErro, setDashboardErro] = useState(false);
  const [tenantSelecionado, setTenantSelecionado] = useState(null);
  const [editPlano, setEditPlano] = useState(null);
  const [senhaReset, setSenhaReset] = useState(null);
  const [resetando, setResetando] = useState(false);
  const [diasExtender, setDiasExtender] = useState(30);
  const [estendendo, setEstendendo] = useState(false);
  const [modalCriar, setModalCriar] = useState(false);
  const [formCriar, setFormCriar] = useState({ nome_empresa: '', email: '', nome: '', plano: 'starter', periodo_cobranca: 'mensal', dias_validade: 30, segmento: '' });
  const [criandoTeste, setCriandoTeste] = useState(false);
  const [resultadoTeste, setResultadoTeste] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [creditoForm, setCreditoForm] = useState({ tipo: 'disparos', quantidade: '', descricao: '' });
  const [creditoModo, setCreditoModo] = useState('conceder'); // 'conceder' | 'reduzir'
  const [reduzindo, setReduzindo] = useState(false);
  const [dataVencimento, setDataVencimento] = useState('');
  const [definindoVencimento, setDefinindoVencimento] = useState(false);
  const [linkPlano, setLinkPlano] = useState({ plano: 'starter', periodo: 'mensal', implantacao: false });
  const [gerandoLink, setGerandoLink] = useState(false);
  const [linkGerado, setLinkGerado] = useState(null); // { url, email, plano, periodo }
  const [destravando, setDestravando] = useState(null);
  const [resultadoDestravar, setResultadoDestravar] = useState({}); // keyed by tenant_id

  const [auditoria, setAuditoria] = useState(null);
  const [auditInstancias, setAuditInstancias] = useState({});
  const [auditSelecao, setAuditSelecao] = useState({});
  const [auditSalvando, setAuditSalvando] = useState({});

  // Anti-ban — blacklist global
  const [blacklist, setBlacklist] = useState([]);
  const [filtroInstanciaBlacklist, setFiltroInstanciaBlacklist] = useState('');
  const [removendoBlacklist, setRemovendoBlacklist] = useState(null);

  // Anti-ban — warmup / cap diário
  const [instanciasConfig, setInstanciasConfig] = useState([]);
  const [salvandoCap, setSalvandoCap] = useState(null);

  // Diagnóstico de agenda
  const [diagAgenda, setDiagAgenda] = useState(null); // { loading, tenant_id, instancia, nome_empresa, resultado }
  const [criarEventoForm, setCriarEventoForm] = useState({ telefone: '', data: '', hora: '', assunto: '', nome: '' });
  const [criandoEvento, setCriandoEvento] = useState(false);

  async function abrirDiagnosticoAgenda(tenant_id, instancia, nome_empresa) {
    setDiagAgenda({ loading: true, tenant_id, instancia, nome_empresa, resultado: null });
    setCriarEventoForm({ telefone: '', data: '', hora: '', assunto: '', nome: '' });
    try {
      const { data } = await api.get('/admin/agenda/diagnostico', { params: { tenant_id, instancia } });
      setDiagAgenda({ loading: false, tenant_id, instancia, nome_empresa, resultado: data });
    } catch (e) {
      setDiagAgenda({ loading: false, tenant_id, instancia, nome_empresa, resultado: { erro: e.response?.data?.erro || e.message } });
    }
  }

  async function criarEventoRetroativo() {
    const { telefone, data, hora, assunto, nome } = criarEventoForm;
    if (!telefone || !data || !hora) {
      toast.error('Preencha telefone, data e hora');
      return;
    }
    const tz = diagAgenda?.resultado?.timezone || 'America/Sao_Paulo';
    // Monta ISO local com offset do timezone (deixa o backend interpretar)
    const dataHora = `${data}T${hora}:00`;
    setCriandoEvento(true);
    try {
      const { data: r } = await api.post('/admin/agenda/criar-evento', {
        tenant_id: diagAgenda.tenant_id,
        instancia: diagAgenda.instancia,
        telefone: telefone.replace(/\D/g, ''),
        data_hora: dataHora,
        assunto: assunto || null,
        nome: nome || null,
        timezone: tz
      }, { timeout: 60000 });
      toast.success(`Evento criado${r.confirmacao_enviada ? ' + WhatsApp enviado ao cliente' : ''}`);
      abrirDiagnosticoAgenda(diagAgenda.tenant_id, diagAgenda.instancia, diagAgenda.nome_empresa);
    } catch (e) {
      toast.error(e.response?.data?.erro || e.message);
    } finally {
      setCriandoEvento(false);
    }
  }

  // Reconciliar instâncias órfãs
  const [reconciliando, setReconciliando] = useState(false);

  // Integrações restritas (Avec etc.)
  const [integracoesAcessos, setIntegracoesAcessos] = useState([]);
  const [novaIntegracao, setNovaIntegracao] = useState({ tenant_id: '', instancia: '', integracao: 'avec' });
  const [salvandoIntegracao, setSalvandoIntegracao] = useState(false);

  // Saldo de créditos — mapa tenant_id → { disparos_usados, disparos_creditos, limite_disparos, ia_usado, ia_creditos, limite_ia }
  const [saldos, setSaldos] = useState(null);
  const [saldosMes, setSaldosMes] = useState('');
  const [saldosErro, setSaldosErro] = useState(null);

  async function carregarSaldos() {
    setSaldosErro(null);
    try {
      const { data } = await api.get('/admin/saldo-creditos');
      const mapa = {};
      for (const r of data.rows) mapa[r.id] = r;
      setSaldos(mapa);
      setSaldosMes(data.mes);
    } catch (e) {
      setSaldos({});
      setSaldosErro(e.response?.status ? `${e.response.status} — ${e.response?.data?.erro || 'erro no servidor'}` : (e.message || 'falha de rede'));
    }
  }

  // Concierge IA — override de persona/base/funil por tenant
  const [iaConfigInstancias, setIaConfigInstancias] = useState([]);
  const [iaConfigInstancia, setIaConfigInstancia] = useState('');
  const [iaConfig, setIaConfig] = useState(null);
  const [iaConfigSalvando, setIaConfigSalvando] = useState(false);
  const [iaQuestionarioAberto, setIaQuestionarioAberto] = useState(false);

  async function carregarIntegracoes() {
    try {
      const { data } = await api.get('/admin/integracoes-acessos');
      setIntegracoesAcessos(data);
    } catch {}
  }

  async function liberarIntegracao() {
    const { tenant_id, instancia, integracao } = novaIntegracao;
    if (!tenant_id || !instancia || !integracao) {
      toast.error('Preencha tenant, instância e integração');
      return;
    }
    setSalvandoIntegracao(true);
    try {
      await api.post('/admin/integracoes-acessos', novaIntegracao);
      toast.success(`Integração ${integracao} liberada`);
      setNovaIntegracao({ tenant_id: '', instancia: '', integracao: 'avec' });
      carregarIntegracoes();
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao liberar');
    } finally {
      setSalvandoIntegracao(false);
    }
  }

  async function revogarIntegracao(tenant_id, instancia, integracao) {
    if (!window.confirm(`Revogar acesso a ${integracao} para esta instância?`)) return;
    try {
      await api.delete('/admin/integracoes-acessos', { data: { tenant_id, instancia, integracao } });
      toast.success('Acesso revogado');
      carregarIntegracoes();
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao revogar');
    }
  }

  async function handleReconciliarInstancias() {
    setReconciliando(true);
    try {
      const { data } = await api.post('/admin/instancias/reconciliar');
      if (data.removidas_orfas > 0) {
        toast.success(`✅ ${data.removidas_orfas} instância(s) órfã(s) removidas. Tenants afetados podem criar novas instâncias.`);
      } else {
        toast.success(`✅ Nenhuma instância órfã encontrada (${data.verificadas} verificadas).`);
      }
      api.get('/admin/instancias-config').then(({ data: d }) => setInstanciasConfig(d)).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao reconciliar instâncias');
    } finally {
      setReconciliando(false);
    }
  }

  async function carregarAuditoria() {
    try {
      const { data } = await api.get('/admin/auditoria/campanhas-vazadas');
      setAuditoria(data);
      const tenantIds = [...new Set(data.map(c => c.dono_tenant_id))];
      const resultados = await Promise.all(
        tenantIds.map(tid => api.get(`/admin/tenants/${tid}/instancias`).then(r => [tid, r.data]))
      );
      const mapa = {};
      resultados.forEach(([tid, instancias]) => { mapa[tid] = instancias.map(i => i.nome); });
      setAuditInstancias(mapa);
    } catch {
      toast.error(t('admin.toast_erro_auditoria'));
    }
  }

  async function handleReatribuir(campanha) {
    const novaInstancia = auditSelecao[campanha.id];
    if (!novaInstancia) { toast.error(t('admin.toast_select_instancia')); return; }
    setAuditSalvando(s => ({ ...s, [campanha.id]: true }));
    try {
      await api.patch(`/admin/campanhas/${campanha.id}/reatribuir-instancia`, { instancia: novaInstancia });
      toast.success(t('admin.toast_reatribuido', { nome: novaInstancia }));
      setAuditoria(prev => prev.filter(c => c.id !== campanha.id));
    } catch (err) {
      toast.error(err.response?.data?.erro || t('admin.toast_reatribuir_erro'));
    } finally {
      setAuditSalvando(s => ({ ...s, [campanha.id]: false }));
    }
  }

  async function carregar() {
    setDashboardErro(false);
    try {
      const { data } = await api.get('/admin/dashboard');
      setDashboard(data);
    } catch (err) {
      const msg = err.response?.data?.erro || err.message;
      toast.error(t('admin.toast_erro_dashboard'));
      console.error('[Admin] dashboard falhou:', msg);
      setDashboardErro(true);
    }
  }

  useEffect(() => {
    carregar();
    api.get('/admin/blacklist').then(({ data }) => setBlacklist(data)).catch(() => {});
    api.get('/admin/instancias-config').then(({ data }) => setInstanciasConfig(data)).catch(() => {});
    carregarIntegracoes();
    carregarSaldos();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLinkGerado(null);
    setLinkPlano({ plano: 'starter', periodo: 'mensal' });
    if (!tenantSelecionado) { setIaConfigInstancias([]); setIaConfigInstancia(''); setIaConfig(null); setIaQuestionarioAberto(false); return; }
    api.get(`/admin/tenants/${tenantSelecionado.id}/instancias`)
      .then(({ data }) => {
        const nomes = data.map(i => i.nome);
        setIaConfigInstancias(nomes);
        if (nomes.length > 0) setIaConfigInstancia(nomes[0]);
        else setIaConfigInstancia('');
      })
      .catch(() => { setIaConfigInstancias([]); setIaConfigInstancia(''); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantSelecionado?.id]);

  useEffect(() => {
    if (!tenantSelecionado || !iaConfigInstancia) { setIaConfig(null); return; }
    setIaConfig(null);
    api.get(`/admin/tenants/${tenantSelecionado.id}/ia-config/${iaConfigInstancia}`)
      .then(({ data }) => setIaConfig(data))
      .catch(() => setIaConfig({ persona: '', base_conhecimento: '', fluxo_qualificacao: '' }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantSelecionado?.id, iaConfigInstancia]);

  async function salvarIAConfig() {
    if (!tenantSelecionado || !iaConfigInstancia || !iaConfig) return;
    setIaConfigSalvando(true);
    try {
      const { data } = await api.put(`/admin/tenants/${tenantSelecionado.id}/ia-config/${iaConfigInstancia}`, iaConfig);
      setIaConfig(data);
      toast.success('Configuração de IA salva com sucesso');
    } catch (e) {
      toast.error(e.response?.data?.erro || 'Erro ao salvar configuração de IA');
    } finally {
      setIaConfigSalvando(false);
    }
  }

  // Ativa/desativa a secretária da instância — salva imediatamente, preservando
  // persona/base/funil atuais (envia o estado completo com ativo alternado).
  async function toggleIAConfigAtivo() {
    if (!tenantSelecionado || !iaConfigInstancia || !iaConfig) return;
    const novoAtivo = !iaConfig.ativo;
    setIaConfigSalvando(true);
    try {
      const { data } = await api.put(`/admin/tenants/${tenantSelecionado.id}/ia-config/${iaConfigInstancia}`, { ...iaConfig, ativo: novoAtivo });
      setIaConfig(data);
      toast.success(novoAtivo ? 'Concierge IA ativada' : 'Concierge IA desativada');
    } catch (e) {
      toast.error(e.response?.data?.erro || 'Erro ao alterar status da IA');
    } finally {
      setIaConfigSalvando(false);
    }
  }

  async function handleRemoverBlacklist(id) {
    setRemovendoBlacklist(id);
    try {
      await api.delete(`/admin/blacklist/${id}`);
      setBlacklist(prev => prev.filter(c => c.id !== id));
      toast.success(t('configuracoes.toast_blacklist_removido'));
    } catch {
      toast.error(t('configuracoes.toast_blacklist_erro'));
    } finally {
      setRemovendoBlacklist(null);
    }
  }

  function calcCapAutomatico(dias) {
    if (dias < 7)  return 50;
    if (dias < 14) return 100;
    if (dias < 21) return 200;
    return 500;
  }

  // Vincula uma instância "não vinculada" a um tenant escolhido
  const [vincularTenant, setVincularTenant] = useState({}); // { [nome]: tenant_id }
  const [vinculando, setVinculando] = useState(null);
  async function handleVincular(nome) {
    const tid = vincularTenant[nome];
    if (!tid) { toast.error('Selecione um tenant'); return; }
    setVinculando(nome);
    try {
      await api.post('/admin/instancias-config/vincular', { nome, tenant_id: tid });
      toast.success(`Instância ${nome} vinculada`);
      const { data } = await api.get('/admin/instancias-config');
      setInstanciasConfig(data);
      setVincularTenant(s => { const cp = { ...s }; delete cp[nome]; return cp; });
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao vincular');
    } finally {
      setVinculando(null);
    }
  }

  async function handleSalvarCap(instancia, tenantId, novoValor) {
    setSalvandoCap(`${instancia}-${tenantId}`);
    try {
      const novoNum = novoValor === '' ? null : Number(novoValor);
      await api.patch('/admin/instancias-config', { nome: instancia, tenant_id: tenantId, cap_diario_override: novoNum });
      setInstanciasConfig(prev => prev.map(i =>
        i.nome === instancia && i.tenant_id === tenantId
          ? { ...i, cap_diario_override: novoNum, cap_atual: novoNum !== null ? novoNum : calcCapAutomatico(i.dias_warmup) }
          : i
      ));
      toast.success(t('configuracoes.toast_cap_atualizado'));
    } catch {
      toast.error(t('configuracoes.toast_cap_erro'));
    } finally {
      setSalvandoCap(null);
    }
  }

  async function handleCriarTeste() {
    if (!formCriar.nome_empresa || !formCriar.email || !formCriar.nome) {
      toast.error(t('admin.toast_campos'));
      return;
    }
    setCriandoTeste(true);
    try {
      const { data } = await api.post('/admin/tenants/criar', formCriar);
      setResultadoTeste(data);
      toast.success(t('admin.toast_criado'));
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.erro || t('admin.toast_erro_criar'));
    } finally {
      setCriandoTeste(false);
    }
  }

  async function handleSalvarOverrides(id) {
    try {
      await api.patch(`/admin/tenants/${id}/overrides`, {
        override_limite_instancias:   overrides.instancias,
        override_limite_contatos:     overrides.contatos,
        override_limite_disparos_mes: overrides.disparos_mes,
        override_limite_ia_mes:       overrides.ia_mes,
      });
      toast.success(t('admin.toast_overrides'));
      carregar();
    } catch { toast.error(t('admin.toast_overrides_erro')); }
  }

  async function handleConcederCreditos(id) {
    if (!creditoForm.quantidade || Number(creditoForm.quantidade) < 1) {
      toast.error(t('admin.toast_creditos_qtd'));
      return;
    }
    try {
      await api.post(`/admin/tenants/${id}/creditos`, {
        tipo: creditoForm.tipo,
        quantidade: Number(creditoForm.quantidade),
        descricao: creditoForm.descricao || undefined,
      });
      toast.success(t('admin.toast_creditos', { n: creditoForm.quantidade, tipo: creditoForm.tipo }));
      setCreditoForm({ tipo: 'disparos', quantidade: '', descricao: '' });
      carregarSaldos();
    } catch { toast.error(t('admin.toast_creditos_erro')); }
  }

  async function handleReduzirCreditos(id) {
    if (!creditoForm.quantidade || Number(creditoForm.quantidade) < 1) {
      toast.error(t('admin.toast_creditos_qtd'));
      return;
    }
    setReduzindo(true);
    try {
      await api.post(`/admin/tenants/${id}/reduzir-creditos`, {
        tipo: creditoForm.tipo,
        quantidade: Number(creditoForm.quantidade),
        descricao: creditoForm.descricao || undefined,
      });
      toast.success(`${creditoForm.quantidade} créditos de ${creditoForm.tipo} removidos`);
      setCreditoForm({ tipo: 'disparos', quantidade: '', descricao: '' });
      carregarSaldos();
    } catch (err) { toast.error(err.response?.data?.erro || t('admin.toast_creditos_erro')); }
    finally { setReduzindo(false); }
  }

  async function handleDefinirVencimento(id) {
    if (!dataVencimento) { toast.error('Selecione uma data'); return; }
    setDefinindoVencimento(true);
    try {
      const { data } = await api.patch(`/admin/tenants/${id}/vencimento`, { data: dataVencimento });
      toast.success(`Vencimento definido para ${new Date(data.data_vencimento).toLocaleDateString('pt-BR')}`);
      setDataVencimento('');
      carregar();
    } catch (err) { toast.error(err.response?.data?.erro || 'Erro ao definir vencimento'); }
    finally { setDefinindoVencimento(false); }
  }

  async function handleGerarLink(id) {
    setGerandoLink(true);
    setLinkGerado(null);
    try {
      const { data } = await api.post(`/admin/tenants/${id}/link-pagamento`, linkPlano);
      setLinkGerado(data);
      toast.success('Link de pagamento gerado');
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao gerar link de pagamento');
    } finally {
      setGerandoLink(false);
    }
  }

  async function handleDestravar(tenantId) {
    setDestravando(tenantId);
    try {
      const { data } = await api.post(`/admin/tenants/${tenantId}/campanhas/destravar`);
      setResultadoDestravar(prev => ({ ...prev, [tenantId]: data }));
      const msgs = [];
      if (data.restauradas > 0) msgs.push(`${data.restauradas} campanha(s) restaurada(s)`);
      if (data.instancias_orfas_limpas > 0) msgs.push(`${data.instancias_orfas_limpas} instância(s) órfã(s) desvinculadas`);
      if (msgs.length === 0) msgs.push('Nenhuma campanha bloqueada encontrada');
      toast.success('✅ ' + msgs.join(' · '));
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao destravar campanhas');
    } finally {
      setDestravando(null);
    }
  }

  async function handleAtivar(id) {
    try {
      await api.patch(`/admin/tenants/${id}/ativar`, { plano: 'starter', periodo: 'mensal' });
      toast.success(t('admin.toast_ativado'));
      carregar();
    } catch { toast.error(t('admin.toast_ativar_erro')); }
  }

  async function handleSuspender(id, nome) {
    if (!window.confirm(t('admin.confirm_suspender', { nome }))) return;
    try {
      await api.patch(`/admin/tenants/${id}/suspender`);
      toast.success(t('admin.toast_suspenso'));
      carregar();
    } catch { toast.error(t('admin.toast_suspender_erro')); }
  }

  async function handleAlterarPlano(id) {
    try {
      await api.patch(`/admin/tenants/${id}/plano`, editPlano);
      toast.success(t('admin.toast_plano_atualizado'));
      setEditPlano(null);
      setTenantSelecionado(null);
      carregar();
    } catch { toast.error(t('admin.toast_plano_erro')); }
  }

  async function handleResetarSenha(id) {
    if (!window.confirm(t('admin.confirm_resetar'))) return;
    setResetando(true);
    setSenhaReset(null);
    try {
      // Passa email_admin e nome_empresa do tenant para permitir criar o usuario caso não exista
      const tenant = dashboard?.tenants?.find(t => t.id === id);
      const body = tenant?.email_admin
        ? { email: tenant.email_admin, nome: tenant.nome_empresa }
        : {};
      const { data } = await api.post(`/admin/tenants/${id}/resetar-senha`, body);
      setSenhaReset(data);
      if (data.criado) toast.success('Usuário criado e senha gerada com sucesso');
    } catch (err) {
      const msg = err.response?.data?.erro;
      if (err.response?.data?.precisa_criar) {
        toast.error('Usuário não encontrado. Clique em "Criar usuário" para recriar o acesso.');
      } else {
        toast.error(msg || t('admin.toast_resetar_erro'));
      }
    }
    finally { setResetando(false); }
  }

  async function handleEstender(id) {
    setEstendendo(true);
    try {
      const { data } = await api.patch(`/admin/tenants/${id}/estender`, { dias: diasExtender });
      toast.success(t('admin.toast_estendido', { data: new Date(data.data_vencimento).toLocaleDateString('pt-BR') }));
      setDiasExtender(30);
      carregar();
    } catch { toast.error(t('admin.toast_estender_erro')); }
    finally { setEstendendo(false); }
  }

  async function handleDeletar(id, nome) {
    if (!window.confirm(t('admin.confirm_deletar', { nome }))) return;
    try {
      await api.delete(`/admin/tenants/${id}`);
      toast.success(t('admin.toast_deletado'));
      carregar();
    } catch { toast.error(t('admin.toast_deletar_erro')); }
  }

  if (!dashboard) {
    return (
      <div>
        <div className="page-h">
          <div>
            <h1 className="page-h-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Shield size={20} color="var(--color-accent)" /> {t('admin.titulo')}
            </h1>
            <p className="page-h-sub">{dashboardErro ? 'Erro ao carregar — backend pode estar indisponível' : t('admin.carregando')}</p>
          </div>
          {dashboardErro && (
            <button onClick={carregar} className="btn btn-primary btn-sm">
              <RefreshCw size={14} /> Tentar novamente
            </button>
          )}
        </div>
        <div className="grid-stats">
          {Array.from({ length: 6 }).map((_, i) => <SkeletonStat key={i} />)}
        </div>
        <div className="card card-flush">
          <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--color-border)' }}>
            <span className="skeleton" style={{ display: 'inline-block', width: 140, height: 16 }} />
          </div>
          <table className="table">
            <thead>
              <tr>
                {[
                  t('admin.col_empresa'),
                  t('admin.col_telefone'),
                  t('admin.col_plano'),
                  t('admin.col_status'),
                  t('admin.col_cadastro'),
                  t('admin.col_ultimo_acesso'),
                  t('admin.col_vencimento'),
                  t('admin.col_acoes'),
                ].map(h => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody><SkeletonTableRows rows={5} cols={8} /></tbody>
          </table>
        </div>
      </div>
    );
  }

  const { metricas, tenants } = dashboard;

  const statusBadge = {
    ativo:    { cls: 'badge badge-success', label: t('admin.status_ativo') },
    pendente: { cls: 'badge badge-warning', label: t('admin.status_pendente') },
    suspenso: { cls: 'badge badge-danger',  label: t('admin.status_suspenso') },
  };

  const cards = [
    { label: t('admin.stat_total_clientes'),    valor: metricas.total_tenants,     icon: <Users size={20} />,       cor: '#16a34a' },
    { label: t('admin.stat_clientes_ativos'),   valor: metricas.tenants_ativos,    icon: <CheckCircle size={20} />, cor: '#22c55e' },
    { label: t('admin.stat_aguardando'),        valor: metricas.tenants_pendentes, icon: <Clock size={20} />,       cor: '#f59e0b' },
    { label: t('admin.stat_total_disparos'),    valor: metricas.total_envios,      icon: <Send size={20} />,        cor: '#3b82f6' },
    { label: t('admin.stat_total_campanhas'),   valor: metricas.total_campanhas,   icon: <Megaphone size={20} />,   cor: '#13305f' },
    { label: t('admin.stat_total_contatos'),    valor: metricas.total_contatos,    icon: <TrendingUp size={20} />,  cor: '#ec4899' },
  ];

  function corVencimento(dt) {
    if (!dt) return null;
    const d = new Date(dt);
    if (d < new Date()) return 'var(--color-danger)';
    if (d < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)) return 'var(--color-warning)';
    return 'var(--color-success)';
  }

  return (
    <div>
      <div className="page-h">
        <div>
          <h1 className="page-h-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Shield size={20} color="var(--color-accent)" /> {t('admin.titulo')}
          </h1>
          <p className="page-h-sub">{t('admin.subtitulo')}</p>
        </div>
        <div className="page-h-actions">
          <button onClick={() => { setModalCriar(true); setResultadoTeste(null); setFormCriar({ nome_empresa: '', email: '', nome: '', plano: 'starter', periodo_cobranca: 'mensal', dias_validade: 30, segmento: '' }); }} className="btn"
            style={{ background: 'linear-gradient(135deg, #16a34a, #13305f)', color: '#fff', borderColor: 'transparent' }}>
            + {t('admin.btn_criar_usuario')}
          </button>
        </div>
      </div>

      <div className="grid-stats">
        {cards.map(({ label, valor, icon, cor }) => (
          <div key={label} className="stat" style={{ flexDirection: 'row', alignItems: 'center', gap: '14px' }}>
            <div style={{ background: cor + '1f', borderRadius: '12px', padding: '10px', color: cor, display: 'grid', placeItems: 'center' }}>
              {icon}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div className="stat-value">{valor}</div>
              <div className="stat-label">{label}</div>
            </div>
          </div>
        ))}
      </div>

      {modalCriar && (
        <div className="modal-backdrop" onClick={() => { setModalCriar(false); setResultadoTeste(null); }}>
          <div onClick={e => e.stopPropagation()} className="modal modal-sm">
            <div className="modal-h">
              <h2 className="modal-title">{t('admin.modal_criar_titulo')}</h2>
              <button onClick={() => { setModalCriar(false); setResultadoTeste(null); }} className="modal-close">×</button>
            </div>

            {resultadoTeste ? (
              <div>
                <div className="info-box info-box-success" style={{ padding: '1.25rem', marginBottom: '1rem' }}>
                  <p style={{ fontSize: '14px', fontWeight: 600, color: 'var(--color-success)', marginBottom: '12px' }}>✅ {t('admin.toast_criado')}</p>
                  <table style={{ width: '100%', fontSize: '13px' }}>
                    <tbody>
                      {[
                        [t('admin.resultado_email'), resultadoTeste.email],
                        [t('admin.resultado_senha'), resultadoTeste.senha_temporaria],
                        [t('admin.resultado_plano'), resultadoTeste.plano],
                        [t('admin.resultado_vencimento'), new Date(resultadoTeste.vencimento).toLocaleDateString('pt-BR')],
                      ].map(([label, valor]) => (
                        <tr key={label}>
                          <td style={{ color: 'var(--color-text-soft)', padding: '4px 0', width: '130px' }}>{label}</td>
                          <td style={{ fontWeight: 600, color: 'var(--color-text)', fontFamily: 'var(--font-mono)' }}>{valor}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="muted" style={{ fontSize: '12px', marginBottom: '1rem' }}>
                  ⚠️ {t('admin.resultado_aviso')}
                </p>
                <div className="row">
                  <button onClick={() => {
                    const texto = `RIIA — Acesso\nEmail: ${resultadoTeste.email}\nSenha: ${resultadoTeste.senha_temporaria}\nAcesse: https://painel.disparaia.net`;
                    navigator.clipboard.writeText(texto);
                    toast.success(t('admin.toast_copiado'));
                  }} className="btn"
                    style={{ flex: 1, background: 'linear-gradient(135deg, #16a34a, #13305f)', color: '#fff', borderColor: 'transparent' }}>
                    📋 {t('admin.btn_copiar_creds')}
                  </button>
                  <button onClick={() => { setModalCriar(false); setResultadoTeste(null); }} className="btn btn-secondary">
                    {t('admin.btn_fechar')}
                  </button>
                </div>
              </div>
            ) : (
              <div>
                {[
                  [t('admin.label_nome_empresa'),      'nome_empresa', 'text',  t('admin.placeholder_nome_empresa')],
                  [t('admin.label_nome_responsavel'),  'nome',         'text',  t('admin.placeholder_nome_responsavel')],
                  [t('admin.label_email'),             'email',        'email', t('admin.placeholder_email')],
                  [t('admin.label_segmento'),          'segmento',     'text',  t('admin.placeholder_segmento')],
                ].map(([label, field, type, placeholder]) => (
                  <div key={field} className="field">
                    <label className="label">{label}</label>
                    <input type={type} placeholder={placeholder}
                      value={formCriar[field]}
                      onChange={e => setFormCriar({ ...formCriar, [field]: e.target.value })}
                      className="input"
                    />
                  </div>
                ))}

                <div className="field">
                  <label className="label">{t('admin.label_plano')}</label>
                  <select value={formCriar.plano}
                    onChange={e => setFormCriar({ ...formCriar, plano: e.target.value })}
                    className="select">
                    <option value="freemium">Freemium (grátis)</option>
                    <option value="starter">Starter — R$97</option>
                    <option value="pro">Profissional — R$797</option>
                    <option value="enterprise">Empresarial — sob consulta</option>
                    <option value="teste">Teste</option>
                  </select>
                </div>

                <div className="field" style={{ marginBottom: '1.5rem' }}>
                  <label className="label">{t('admin.label_dias_validade')}</label>
                  <select value={formCriar.dias_validade}
                    onChange={e => setFormCriar({ ...formCriar, dias_validade: Number(e.target.value) })}
                    className="select">
                    <option value={7}>7 dias</option>
                    <option value={14}>14 dias</option>
                    <option value={30}>30 dias</option>
                    <option value={60}>60 dias</option>
                    <option value={90}>90 dias</option>
                  </select>
                </div>

                <div className="row">
                  <button onClick={handleCriarTeste} disabled={criandoTeste} className="btn"
                    style={{ flex: 1, background: 'linear-gradient(135deg, #16a34a, #13305f)', color: '#fff', borderColor: 'transparent', padding: '11px' }}>
                    {criandoTeste ? t('admin.btn_criando') : '✓ ' + t('admin.btn_criar')}
                  </button>
                  <button onClick={() => setModalCriar(false)} className="btn btn-secondary">
                    {t('admin.btn_cancelar')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {tenantSelecionado && (
        <div className="modal-backdrop" onClick={() => { setTenantSelecionado(null); setEditPlano(null); setSenhaReset(null); }}>
          <div onClick={e => e.stopPropagation()} className="modal modal-sm">
            <div className="modal-h">
              <h2 className="modal-title">{t('admin.modal_gerenciar', { nome: tenantSelecionado.nome_empresa })}</h2>
              <button onClick={() => { setTenantSelecionado(null); setEditPlano(null); setSenhaReset(null); }} className="modal-close">×</button>
            </div>

            <div className="field">
              <label className="label">{t('admin.label_plano')}</label>
              <select value={editPlano?.plano || tenantSelecionado.plano}
                onChange={e => setEditPlano({ ...editPlano, plano: e.target.value })}
                className="select">
                <option value="freemium">Freemium</option>
                <option value="starter">Starter</option>
                <option value="pro">Profissional</option>
                <option value="enterprise">Empresarial</option>
              </select>
            </div>

            <div className="field" style={{ marginBottom: '1.5rem' }}>
              <label className="label">{t('admin.label_periodo')}</label>
              <select value={editPlano?.periodo_cobranca || tenantSelecionado.periodo_cobranca}
                onChange={e => setEditPlano({ ...editPlano, periodo_cobranca: e.target.value })}
                className="select">
                <option value="mensal">{t('admin.periodo_mensal')}</option>
                <option value="anual">{t('admin.periodo_anual')}</option>
                <option value="teste">{t('admin.periodo_teste')}</option>
              </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
              {[
                [t('admin.col_campanhas'),    tenantSelecionado.campanhas],
                [t('admin.col_contatos'),     tenantSelecionado.contatos],
                [t('admin.stat_envios'),      tenantSelecionado.envios],
                [t('admin.stat_email_admin'), tenantSelecionado.email_admin],
              ].map(([label, valor]) => (
                <div key={label} style={{ background: 'var(--color-bg-soft)', borderRadius: 'var(--radius)', padding: '10px 12px', border: '1px solid var(--color-border-soft)' }}>
                  <div style={{ fontSize: '11px', color: 'var(--color-text-muted)' }}>{label}</div>
                  <div style={{ fontSize: label === t('admin.stat_email_admin') ? '12px' : '16px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>{valor}</div>
                </div>
              ))}
            </div>

            <div className="row" style={{ marginBottom: '1.25rem' }}>
              <button onClick={() => handleAlterarPlano(tenantSelecionado.id)} className="btn btn-primary" style={{ flex: 1 }}>
                {t('admin.btn_salvar_plano')}
              </button>
              <button onClick={() => { setTenantSelecionado(null); setEditPlano(null); setSenhaReset(null); }} className="btn btn-secondary">
                {t('admin.btn_cancelar')}
              </button>
            </div>

            <hr style={{ margin: '0 0 1.25rem' }} />

            <div style={{ marginBottom: '1.25rem' }}>
              <p style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>📅 {t('admin.sec_estender')}</p>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <select value={diasExtender} onChange={e => setDiasExtender(Number(e.target.value))} className="select" style={{ flex: 1 }}>
                  {[7, 15, 30, 60, 90, 180, 365].map(d => (
                    <option key={d} value={d}>+ {d} dias</option>
                  ))}
                </select>
                <button onClick={() => handleEstender(tenantSelecionado.id)} disabled={estendendo} className="btn btn-secondary">
                  {estendendo ? t('admin.btn_salvando') : t('admin.btn_aplicar')}
                </button>
              </div>
              {tenantSelecionado.data_vencimento && (
                <p style={{ fontSize: '11px', color: 'var(--color-text-muted)', marginTop: '6px' }}>
                  {t('admin.vencimento_atual')}: <strong>{new Date(tenantSelecionado.data_vencimento).toLocaleDateString('pt-BR')}</strong>
                </p>
              )}
              <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px dashed var(--color-border-soft)' }}>
                <p style={{ fontSize: '11px', color: 'var(--color-text-muted)', marginBottom: '6px' }}>Ou definir data específica:</p>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input type="date" value={dataVencimento} onChange={e => setDataVencimento(e.target.value)}
                    className="input" style={{ flex: 1, padding: '6px 10px', fontSize: '13px' }} />
                  <button onClick={() => handleDefinirVencimento(tenantSelecionado.id)}
                    disabled={definindoVencimento || !dataVencimento} className="btn btn-secondary">
                    {definindoVencimento ? t('admin.btn_salvando') : 'Definir'}
                  </button>
                </div>
              </div>
            </div>

            <hr style={{ margin: '0 0 1.25rem' }} />

            <div style={{ marginBottom: '1.25rem' }}>
              <p style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>💳 Gerar link de pagamento</p>
              <p style={{ fontSize: '11px', color: 'var(--color-text-muted)', marginBottom: '10px' }}>
                Cria um link de checkout (Stripe) para este cliente. Ao pagar, o plano é ativado e o vencimento renovado automaticamente — mesmo fluxo da landing.
              </p>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                <select value={linkPlano.plano} onChange={e => setLinkPlano(p => ({ ...p, plano: e.target.value }))} className="select" style={{ flex: 1 }}>
                  <option value="starter">Starter</option>
                  <option value="essencial">Essencial</option>
                  <option value="pro">Profissional</option>
                  <option value="performance1">Performance 1 (R$1.297)</option>
                  <option value="performance2">Performance 2 (R$997)</option>
                </select>
                <select value={linkPlano.periodo} onChange={e => setLinkPlano(p => ({ ...p, periodo: e.target.value }))} className="select" style={{ width: 110 }}>
                  <option value="mensal">Mensal</option>
                  <option value="anual">Anual</option>
                </select>
              </div>
              <label style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px', fontSize: '12px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={linkPlano.implantacao}
                  onChange={e => setLinkPlano(p => ({ ...p, implantacao: e.target.checked }))}
                />
                Incluir implantação assistida (+R$ 300, cobrança única)
              </label>
              <button onClick={() => handleGerarLink(tenantSelecionado.id)} disabled={gerandoLink} className="btn btn-secondary" style={{ width: '100%' }}>
                {gerandoLink ? 'Gerando...' : '🔗 Gerar link de pagamento'}
              </button>
              {linkGerado && (
                <div className="info-box info-box-success" style={{ padding: '10px 12px', marginTop: '10px' }}>
                  <p style={{ fontSize: '12px', margin: '0 0 6px', color: 'var(--color-success)', fontWeight: 600 }}>
                    ✅ Link gerado para {linkGerado.email}
                  </p>
                  <input readOnly value={linkGerado.url} onClick={e => e.target.select()}
                    className="input" style={{ fontSize: '11px', fontFamily: 'var(--font-mono)', marginBottom: '8px' }} />
                  <button onClick={() => { navigator.clipboard.writeText(linkGerado.url); toast.success(t('admin.toast_copiado')); }}
                    className="btn btn-secondary btn-sm" style={{ width: '100%' }}>
                    📋 Copiar link
                  </button>
                </div>
              )}
            </div>

            <hr style={{ margin: '0 0 1.25rem' }} />

            <div>
              <p style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>🔑 {t('admin.sec_senha')}</p>
              {senhaReset ? (
                <div className="info-box info-box-success" style={{ padding: '12px', marginBottom: '8px' }}>
                  <p style={{ fontSize: '12px', margin: '0 0 8px', color: 'var(--color-success)', fontWeight: 600 }}>✅ {t('admin.senha_resetada')}</p>
                  <table style={{ fontSize: '12px', width: '100%' }}>
                    <tbody>
                      <tr>
                        <td style={{ color: 'var(--color-text-muted)', paddingBottom: 4, width: 60 }}>{t('admin.resultado_email')}</td>
                        <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{senhaReset.email}</td>
                      </tr>
                      <tr>
                        <td style={{ color: 'var(--color-text-muted)' }}>{t('admin.resultado_senha')}</td>
                        <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{senhaReset.nova_senha}</td>
                      </tr>
                    </tbody>
                  </table>
                  <button onClick={() => {
                    navigator.clipboard.writeText(`Email: ${senhaReset.email}\nSenha: ${senhaReset.nova_senha}`);
                    toast.success(t('admin.toast_copiado'));
                  }} className="btn btn-secondary btn-sm" style={{ marginTop: '8px', width: '100%' }}>
                    📋 {t('admin.btn_copiar')}
                  </button>
                </div>
              ) : (
                <button onClick={() => handleResetarSenha(tenantSelecionado.id)} disabled={resetando} className="btn btn-secondary" style={{ width: '100%' }}>
                  {resetando ? t('admin.btn_gerando') : '🔑 ' + t('admin.btn_gerar_senha')}
                </button>
              )}
              <p style={{ fontSize: '11px', color: 'var(--color-text-muted)', marginTop: '6px' }}>
                {t('admin.senha_aviso')}
              </p>
            </div>

            <hr style={{ margin: '0 0 1.25rem' }} />

            <div>
              <p style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>⚙️ {t('admin.sec_overrides')}</p>
              <p style={{ fontSize: '11px', color: 'var(--color-text-muted)', marginBottom: '10px' }}>
                {t('admin.override_aviso')}
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '10px' }}>
                {[
                  [t('admin.label_instancias'),    'instancias'],
                  [t('admin.label_contatos'),      'contatos'],
                  [t('admin.label_disparos_mes'),  'disparos_mes'],
                  [t('admin.label_ia_mes'),        'ia_mes'],
                ].map(([label, campo]) => (
                  <div key={campo}>
                    <label style={{ fontSize: '11px', color: 'var(--color-text-muted)', display: 'block', marginBottom: '3px' }}>{label}</label>
                    <input
                      type="number" min="0"
                      placeholder={t('admin.placeholder_padrao')}
                      value={overrides[campo] ?? ''}
                      onChange={e => setOverrides(o => ({ ...o, [campo]: e.target.value === '' ? null : Number(e.target.value) }))}
                      className="input"
                      style={{ fontSize: '13px', padding: '6px 10px' }}
                    />
                  </div>
                ))}
              </div>
              <button onClick={() => handleSalvarOverrides(tenantSelecionado.id)} className="btn btn-secondary" style={{ width: '100%' }}>
                {t('admin.btn_salvar_overrides')}
              </button>
            </div>

            <hr style={{ margin: '1.25rem 0' }} />

            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <p style={{ fontSize: '13px', fontWeight: 600, margin: 0 }}>🎁 {t('admin.sec_creditos')}</p>
                <div style={{ display: 'flex', gap: '4px', background: 'var(--color-bg-soft)', borderRadius: '6px', padding: '2px' }}>
                  {[['conceder', '+ Conceder'], ['reduzir', '− Reduzir']].map(([modo, label]) => (
                    <button key={modo} onClick={() => setCreditoModo(modo)}
                      style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '4px', border: 'none', cursor: 'pointer',
                        background: creditoModo === modo ? (modo === 'reduzir' ? 'var(--color-danger)' : 'var(--color-primary)') : 'transparent',
                        color: creditoModo === modo ? '#fff' : 'var(--color-text-muted)', fontWeight: creditoModo === modo ? 600 : 400 }}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              {(() => {
                const sb = saldos?.[tenantSelecionado.id];
                if (!sb) return null;
                const mes = saldosMes ? `${saldosMes.slice(5, 7)}/${saldosMes.slice(0, 4)}` : '';
                return (
                  <div style={{ background: 'var(--color-bg-soft)', borderRadius: 'var(--radius)', padding: '10px 12px', marginBottom: '10px', border: '1px solid var(--color-border-soft)', fontSize: '12px' }}>
                    <div style={{ fontWeight: 600, marginBottom: '6px', color: 'var(--color-text-muted)', fontSize: '11px' }}>Saldo atual{mes ? ` (${mes})` : ''}</div>
                    {[
                      ['📤 Disparos', sb.disparos_usados, sb.limite_disparos, sb.disparos_creditos],
                      ['🤖 Msgs IA',  sb.ia_usado,        sb.limite_ia,       sb.ia_creditos],
                    ].map(([label, usado, limite, creditos]) => (
                      <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <span style={{ color: 'var(--color-text)' }}>{label}</span>
                        <span>
                          <span style={{ fontWeight: 600 }}>{usado.toLocaleString('pt-BR')}</span>
                          <span style={{ color: 'var(--color-text-muted)' }}> / {limite.toLocaleString('pt-BR')}</span>
                          {creditos > 0 && <span style={{ marginLeft: '6px', background: '#f0fdf4', color: '#15803d', borderRadius: '4px', padding: '1px 6px', fontSize: '11px' }}>+{creditos.toLocaleString('pt-BR')} créditos</span>}
                        </span>
                      </div>
                    ))}
                  </div>
                );
              })()}
              <p style={{ fontSize: '11px', color: 'var(--color-text-muted)', marginBottom: '10px' }}>
                {creditoModo === 'reduzir' ? 'Remove créditos avulsos do mês atual (da linha mais antiga para a mais nova).' : t('admin.creditos_aviso')}
              </p>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                <select value={creditoForm.tipo} onChange={e => setCreditoForm(f => ({ ...f, tipo: e.target.value }))} className="select" style={{ flex: 1 }}>
                  <option value="disparos">{t('admin.tipo_disparos')}</option>
                  <option value="ia">{t('admin.tipo_ia')}</option>
                </select>
                <input type="number" min="1" placeholder={t('admin.placeholder_qtd')}
                  value={creditoForm.quantidade}
                  onChange={e => setCreditoForm(f => ({ ...f, quantidade: e.target.value }))}
                  className="input" style={{ width: '80px', padding: '6px 10px', fontSize: '13px' }} />
              </div>
              <input type="text" placeholder={t('admin.placeholder_desc_credito')}
                value={creditoForm.descricao}
                onChange={e => setCreditoForm(f => ({ ...f, descricao: e.target.value }))}
                className="input" style={{ marginBottom: '8px', fontSize: '13px' }} />
              {creditoModo === 'conceder' ? (
                <button onClick={() => handleConcederCreditos(tenantSelecionado.id)} className="btn btn-secondary" style={{ width: '100%' }}>
                  {t('admin.btn_conceder')}
                </button>
              ) : (
                <button onClick={() => handleReduzirCreditos(tenantSelecionado.id)} disabled={reduzindo}
                  className="btn" style={{ width: '100%', background: 'var(--color-danger)', color: '#fff', border: 'none' }}>
                  {reduzindo ? t('admin.btn_salvando') : `− Reduzir créditos`}
                </button>
              )}
            </div>

            <hr style={{ margin: '1.25rem 0' }} />

            <div>
              <p style={{ fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>🔓 Destravar campanhas</p>
              <p style={{ fontSize: '11px', color: 'var(--color-text-muted)', marginBottom: '10px' }}>
                Restaura campanhas soft-deletadas e desvincula instâncias órfãs (após migração do Evolution). O cliente pode reatribuir a instância no painel depois.
              </p>
              {resultadoDestravar[tenantSelecionado.id] && (
                <div className="info-box info-box-success" style={{ padding: '10px 12px', marginBottom: '10px', fontSize: 12 }}>
                  {resultadoDestravar[tenantSelecionado.id].restauradas > 0 && (
                    <div>✅ <strong>{resultadoDestravar[tenantSelecionado.id].restauradas}</strong> campanha(s) restaurada(s):&nbsp;
                      {resultadoDestravar[tenantSelecionado.id].detalhe_restauradas.map(c => c.nome).join(', ')}
                    </div>
                  )}
                  {resultadoDestravar[tenantSelecionado.id].instancias_orfas_limpas > 0 && (
                    <div>🔗 <strong>{resultadoDestravar[tenantSelecionado.id].instancias_orfas_limpas}</strong> instância(s) órfã(s) desvinculadas:&nbsp;
                      {resultadoDestravar[tenantSelecionado.id].detalhe_limpas.map(c => c.nome).join(', ')}
                    </div>
                  )}
                  {resultadoDestravar[tenantSelecionado.id].restauradas === 0 && resultadoDestravar[tenantSelecionado.id].instancias_orfas_limpas === 0 && (
                    <div>ℹ️ Nenhuma campanha bloqueada encontrada.</div>
                  )}
                </div>
              )}
              <button
                onClick={() => handleDestravar(tenantSelecionado.id)}
                disabled={destravando === tenantSelecionado.id}
                className="btn btn-secondary"
                style={{ width: '100%' }}
              >
                {destravando === tenantSelecionado.id ? 'Destravando...' : '🔓 Destravar campanhas'}
              </button>
            </div>

            <hr style={{ margin: '0 0 1.25rem' }} />

            <div>
              <p style={{ fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>🤖 Concierge IA — Persona / Base / Funil</p>
              <p style={{ fontSize: '11px', color: 'var(--color-text-muted)', marginBottom: '10px' }}>
                Edita a configuração desta instância. O usuário continua podendo ajustar no painel dele.
              </p>

              {iaConfigInstancias.length === 0 ? (
                <p style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>Sem instâncias cadastradas.</p>
              ) : (
                <>
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ fontSize: '11px', color: 'var(--color-text-muted)', display: 'block', marginBottom: '3px' }}>Instância</label>
                    <select
                      value={iaConfigInstancia}
                      onChange={e => setIaConfigInstancia(e.target.value)}
                      className="select"
                    >
                      {iaConfigInstancias.map(i => <option key={i} value={i}>{i}</option>)}
                    </select>
                  </div>

                  {!iaConfig ? (
                    <p style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>Carregando...</p>
                  ) : (
                    <>
                      <div
                        onClick={() => { if (!iaConfigSalvando) toggleIAConfigAtivo(); }}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '10px 12px', borderRadius: 'var(--radius)', marginBottom: '12px',
                          cursor: iaConfigSalvando ? 'wait' : 'pointer',
                          border: `1px solid ${iaConfig.ativo ? 'var(--color-success)' : 'var(--color-border)'}`,
                          background: 'var(--color-bg-soft)'
                        }}
                      >
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: 600 }}>Concierge IA</div>
                          <div style={{ fontSize: '11px', color: iaConfig.ativo ? 'var(--color-success)' : 'var(--color-text-muted)', fontWeight: 500 }}>
                            {iaConfig.ativo ? 'Ativa — responde automaticamente' : 'Inativa'}
                          </div>
                        </div>
                        <div style={{ width: 44, height: 24, borderRadius: 12, position: 'relative', flexShrink: 0, background: iaConfig.ativo ? 'var(--color-success)' : '#cbd5e1', transition: 'background 0.2s' }}>
                          <div style={{ position: 'absolute', top: 3, left: iaConfig.ativo ? 22 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                        </div>
                      </div>
                      {/* Configuração preenchida pelo usuário — read-only */}
                      <div style={{ marginBottom: '12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
                          <button
                            onClick={() => setIaQuestionarioAberto(v => !v)}
                            style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: 'var(--color-bg-soft)', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 600, color: 'var(--color-text)' }}
                          >
                            <span>📋 Ver configuração do usuário</span>
                            <span style={{ fontSize: '10px', color: 'var(--color-text-muted)' }}>{iaQuestionarioAberto ? '▲ recolher' : '▼ expandir'}</span>
                          </button>
                          {iaQuestionarioAberto && (() => {
                            const q = iaConfig.questionario || {};
                            const TIPO_EMOJI = { saude: '🏥', beleza: '✂️', alimentacao: '🍽️', educacao: '📚', comercio: '🛍️', servicos: '⚙️', imoveis: '🏠', juridico: '⚖️', outro: '💬' };
                            const TIPO_NOME  = { saude: 'Saúde & Clínicas', beleza: 'Beleza & Estética', alimentacao: 'Alimentação', educacao: 'Educação & Cursos', comercio: 'Comércio', servicos: 'Serviços', imoveis: 'Imóveis', juridico: 'Jurídico & Financeiro', outro: 'Outro' };
                            const ESTILO_NOME = { humana: 'Humana e acolhedora', direta: 'Direta e objetiva', persuasiva: 'Persuasiva', profissional: 'Profissional e formal', descontraida: 'Descontraída e amigável' };
                            const OBJ_NOME   = { agendar: 'Agendar consultas/visitas', qualificar: 'Entender o caso', vender: 'Fechar vendas', informar: 'Tirar dúvidas' };
                            const rows = [
                              iaConfig.persona_tipo && ['Tipo de negócio', `${TIPO_EMOJI[iaConfig.persona_tipo] || ''} ${TIPO_NOME[iaConfig.persona_tipo] || iaConfig.persona_tipo}`],
                              q.estilo          && ['Estilo', ESTILO_NOME[q.estilo] || q.estilo],
                              q.nome_secretaria && ['Nome da secretária', q.nome_secretaria],
                              q.objetivo_conversa && ['Objetivo', OBJ_NOME[q.objetivo_conversa] || q.objetivo_conversa],
                              q.descricao       && ['Descrição do negócio', q.descricao],
                              q.publico_alvo    && ['Público-alvo', q.publico_alvo],
                              q.servicos        && ['Serviços/produtos', q.servicos],
                              q.dados_coletar?.length && ['Dados a coletar', Array.isArray(q.dados_coletar) ? q.dados_coletar.join(', ') : q.dados_coletar],
                              q.horario         && ['Horário de funcionamento', q.horario],
                              q.contato?.length && ['Formas de contato', Array.isArray(q.contato) ? q.contato.join(', ') : q.contato],
                              q.pagamento?.length && ['Formas de pagamento', Array.isArray(q.pagamento) ? q.pagamento.join(', ') : q.pagamento],
                              q.profissional    && ['Profissional/equipe', q.profissional],
                              q.diferencial     && ['Diferenciais', q.diferencial],
                              q.restricoes      && ['Restrições/avisos', q.restricoes],
                              q.localizacao     && ['Localização', q.localizacao],
                              q.tem_cursos      && ['Cursos', q.cursos_descricao || 'Sim'],
                            ].filter(Boolean);
                            return (
                              <div style={{ padding: '10px 12px', background: '#fff' }}>
                                {rows.length === 0 ? (
                                  <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', margin: 0 }}>Usuário ainda não preencheu o questionário.</p>
                                ) : rows.map(([label, valor]) => (
                                  <div key={label} style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: '4px', marginBottom: '6px', fontSize: '11px' }}>
                                    <span style={{ color: 'var(--color-text-muted)', fontWeight: 600, paddingTop: '1px' }}>{label}</span>
                                    <span style={{ color: 'var(--color-text)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{valor}</span>
                                  </div>
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                      <div style={{ marginBottom: '8px' }}>
                        <label style={{ fontSize: '11px', color: 'var(--color-text-muted)', display: 'block', marginBottom: '3px' }}>Persona</label>
                        <textarea
                          rows={4}
                          value={iaConfig.persona || ''}
                          onChange={e => setIaConfig(c => ({ ...c, persona: e.target.value }))}
                          className="input"
                          style={{ resize: 'vertical', fontFamily: 'var(--font-mono)', fontSize: '11px', width: '100%' }}
                        />
                      </div>
                      <div style={{ marginBottom: '8px' }}>
                        <label style={{ fontSize: '11px', color: 'var(--color-text-muted)', display: 'block', marginBottom: '3px' }}>Base de Conhecimento</label>
                        <textarea
                          rows={4}
                          value={iaConfig.base_conhecimento || ''}
                          onChange={e => setIaConfig(c => ({ ...c, base_conhecimento: e.target.value }))}
                          className="input"
                          style={{ resize: 'vertical', fontFamily: 'var(--font-mono)', fontSize: '11px', width: '100%' }}
                        />
                      </div>
                      <div style={{ marginBottom: '8px' }}>
                        <label style={{ fontSize: '11px', color: 'var(--color-text-muted)', display: 'block', marginBottom: '3px' }}>Funil de Qualificação</label>
                        <textarea
                          rows={4}
                          value={iaConfig.fluxo_qualificacao || ''}
                          onChange={e => setIaConfig(c => ({ ...c, fluxo_qualificacao: e.target.value }))}
                          className="input"
                          style={{ resize: 'vertical', fontFamily: 'var(--font-mono)', fontSize: '11px', width: '100%' }}
                        />
                      </div>
                      <button
                        onClick={salvarIAConfig}
                        disabled={iaConfigSalvando}
                        className="btn btn-primary"
                        style={{ width: '100%' }}
                      >
                        {iaConfigSalvando ? 'Salvando...' : '💾 Salvar configuração IA'}
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="card card-flush" style={{ marginBottom: '24px' }}>
        <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <AlertTriangle size={16} color="#f59e0b" />
            <span style={{ fontSize: '14px', fontWeight: 600 }}>{t('admin.auditoria_titulo')}</span>
            {auditoria && auditoria.length > 0 && (
              <span style={{ background: '#fef3c7', color: '#92400e', borderRadius: '20px', padding: '2px 10px', fontSize: '12px', fontWeight: 600 }}>
                {auditoria.length}
              </span>
            )}
          </div>
          <button onClick={carregarAuditoria} className="btn btn-secondary btn-sm">
            {auditoria === null ? t('admin.auditoria_verificar') : t('admin.auditoria_atualizar')}
          </button>
        </div>

        {auditoria === null ? (
          <div className="empty" style={{ padding: '24px', color: 'var(--color-text-muted)', fontSize: '13px' }}>
            {t('admin.auditoria_vazio_info')}
          </div>
        ) : auditoria.length === 0 ? (
          <div className="empty" style={{ padding: '24px', fontSize: '13px', color: 'var(--color-success)' }}>
            ✅ {t('admin.auditoria_ok')}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>{t('admin.col_campanha')}</th>
                  <th>{t('admin.col_empresa_dona')}</th>
                  <th>{t('admin.col_instancia_errada')}</th>
                  <th>{t('admin.col_dono_instancia')}</th>
                  <th>{t('admin.col_enviados')}</th>
                  <th>{t('admin.col_status')}</th>
                  <th>{t('admin.col_nova_instancia')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {auditoria.map(c => {
                  const opcoesInstancias = auditInstancias[c.dono_tenant_id] || [];
                  return (
                    <tr key={c.id}>
                      <td style={{ fontWeight: 500, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.nome}
                      </td>
                      <td style={{ fontSize: '13px' }}>{c.dono_empresa || '—'}</td>
                      <td>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', background: '#fef2f2', color: '#b91c1c', borderRadius: '4px', padding: '2px 6px' }}>
                          {c.instancia}
                        </span>
                      </td>
                      <td style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>
                        {c.instancia_dono_empresa || <span style={{ color: '#b91c1c' }}>⚠ sem dono</span>}
                      </td>
                      <td style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>{c.enviados}</td>
                      <td>
                        <span className={`badge ${c.status === 'concluida' ? 'badge-success' : c.status === 'ativa' ? 'badge-neutral' : 'badge-neutral'}`}>
                          {c.status}
                        </span>
                      </td>
                      <td>
                        {opcoesInstancias.length === 0 ? (
                          <span style={{ fontSize: '12px', color: '#b91c1c' }}>{t('admin.sem_instancia_disponivel')}</span>
                        ) : (
                          <select
                            value={auditSelecao[c.id] || ''}
                            onChange={e => setAuditSelecao(s => ({ ...s, [c.id]: e.target.value }))}
                            className="select"
                            style={{ minWidth: 160, fontSize: '13px', padding: '5px 8px' }}
                          >
                            <option value="">{t('admin.selecionar_placeholder')}</option>
                            {opcoesInstancias.map(nome => (
                              <option key={nome} value={nome}>{nome}</option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td>
                        <button
                          onClick={() => handleReatribuir(c)}
                          disabled={!auditSelecao[c.id] || auditSalvando[c.id]}
                          className="btn btn-sm"
                          style={{ background: '#f0fdf4', color: 'var(--color-success)', borderColor: '#86efac', whiteSpace: 'nowrap' }}
                        >
                          {auditSalvando[c.id] ? t('admin.btn_salvando') : t('admin.btn_reatribuir')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card card-flush">
        <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '14px', fontWeight: 600 }}>{t('admin.clientes', { n: tenants.length })}</span>
          <button onClick={() => exportarCSV(tenants)} className="btn btn-secondary btn-sm">
            ⬇ Exportar todos CSV
          </button>
        </div>
        <table className="table">
          <thead>
            <tr>
              {[
                t('admin.col_empresa'),
                t('admin.col_telefone'),
                t('admin.col_plano'),
                t('admin.col_status'),
                t('admin.col_cadastro'),
                t('admin.col_ultimo_acesso'),
                t('admin.col_vencimento'),
                t('admin.col_acoes'),
              ].map(h => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tenants.map(tenant => {
              const sb = statusBadge[tenant.status] || { cls: 'badge badge-neutral', label: tenant.status };
              const pb = planoBadge[tenant.plano] || { bg: '#f1f2f7', color: 'var(--color-text-soft)' };
              const cv = corVencimento(tenant.data_vencimento);
              return (
                <tr key={tenant.id}>
                  <td style={{ fontWeight: 500 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {tenant.logo_url
                        ? <img src={tenant.logo_url} alt="" style={{ width: '24px', height: '24px', borderRadius: '4px', objectFit: 'cover' }} />
                        : <div style={{ width: '24px', height: '24px', borderRadius: '4px', background: tenant.cor_primaria || '#13305f', display: 'grid', placeItems: 'center', color: '#fff', fontSize: '10px', fontWeight: 600 }}>
                            {tenant.nome_empresa?.charAt(0).toUpperCase()}
                          </div>
                      }
                      {tenant.nome_empresa}
                    </div>
                  </td>
                  <td className="table-cell-muted" style={{ fontSize: '12px' }}>{formatarTelefone(tenant.celular)}</td>
                  <td>
                    <span className="badge" style={{ background: pb.bg, color: pb.color }}>
                      {tenant.plano} · {tenant.periodo_cobranca}
                    </span>
                  </td>
                  <td>
                    <span className={sb.cls}>{sb.label}</span>
                  </td>
                  <td className="table-cell-muted" style={{ fontSize: '12px' }}>
                    {new Date(tenant.created_at).toLocaleDateString('pt-BR')}
                  </td>
                  <td style={{ fontSize: '12px' }}>
                    {(() => {
                      const ua = formatarUltimoAcesso(tenant.ultimo_acesso);
                      return (
                        <span style={{ fontWeight: 500, color: ua.cor }}>
                          {ua.texto}
                          {ua.relativo && <span style={{ display: 'block', fontSize: '11px', fontWeight: 400, color: 'var(--color-text-muted)' }}>{ua.relativo}</span>}
                        </span>
                      );
                    })()}
                  </td>
                  <td>
                    {tenant.data_vencimento ? (
                      <span style={{ fontSize: '12px', fontWeight: 500, color: cv }}>
                        {new Date(tenant.data_vencimento).toLocaleDateString('pt-BR')}
                        {cv === 'var(--color-danger)' && ' ⚠️'}
                        {cv === 'var(--color-warning)' && ' ⏳'}
                      </span>
                    ) : <span className="muted" style={{ fontSize: '12px' }}>—</span>}
                  </td>
                  <td>
                    <div className="row-tight" style={{ flexWrap: 'wrap', gap: '6px' }}>
                      <button onClick={() => exportarCSVCliente(tenant)} className="btn btn-secondary btn-sm" title="Exportar dados do cliente">
                        ⬇
                      </button>
                      <button onClick={() => {
                        setTenantSelecionado(tenant);
                        setEditPlano({ plano: tenant.plano, periodo_cobranca: tenant.periodo_cobranca });
                        setOverrides({
                          instancias:   tenant.override_limite_instancias   ?? null,
                          contatos:     tenant.override_limite_contatos     ?? null,
                          disparos_mes: tenant.override_limite_disparos_mes ?? null,
                          ia_mes:       tenant.override_limite_ia_mes       ?? null,
                        });
                        setCreditoForm({ tipo: 'disparos', quantidade: '', descricao: '' });
                      }} className="btn btn-secondary btn-sm">
                        {t('admin.btn_gerenciar')}
                      </button>
                      {tenant.status === 'pendente' && (
                        <button onClick={() => handleAtivar(tenant.id)} className="btn btn-sm" style={{ background: '#f0fdf4', color: 'var(--color-success)', borderColor: '#86efac' }}>
                          {t('admin.btn_ativar')}
                        </button>
                      )}
                      {tenant.status === 'ativo' && (
                        <button onClick={() => handleSuspender(tenant.id, tenant.nome_empresa)} className="btn btn-warning btn-sm">
                          {t('admin.btn_suspender')}
                        </button>
                      )}
                      {tenant.status === 'suspenso' && (
                        <button onClick={() => handleAtivar(tenant.id)} className="btn btn-sm" style={{ background: '#f0fdf4', color: 'var(--color-success)', borderColor: '#86efac' }}>
                          {t('admin.btn_reativar')}
                        </button>
                      )}
                      <button onClick={() => handleDeletar(tenant.id, tenant.nome_empresa)} className="btn btn-danger btn-sm">
                        {t('admin.btn_deletar')}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {tenants.length === 0 && (
              <tr><td colSpan={8}>
                <div className="empty">{t('admin.vazio')}</div>
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ─── Saldo de Créditos ─────────────────────────────────────── */}
      <div className="card card-flush" style={{ marginTop: '1.5rem' }}>
        <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '14px', fontWeight: 600 }}>💳 Saldo de Créditos{saldosMes ? ` — ${saldosMes.slice(5, 7)}/${saldosMes.slice(0, 4)}` : ''}</span>
          <button onClick={carregarSaldos} className="btn btn-secondary btn-sm">
            <RefreshCw size={13} />
          </button>
        </div>
        {!saldos ? (
          <div className="empty" style={{ padding: '20px', fontSize: '13px', color: 'var(--color-text-muted)' }}>Carregando...</div>
        ) : saldosErro ? (
          <div className="empty" style={{ padding: '20px', fontSize: '13px', color: 'var(--color-danger)' }}>
            Erro ao carregar saldos: {saldosErro}
            <button onClick={carregarSaldos} className="btn btn-secondary btn-sm" style={{ marginLeft: 10 }}>
              <RefreshCw size={12} /> Tentar novamente
            </button>
          </div>
        ) : Object.keys(saldos).length === 0 ? (
          <div className="empty" style={{ padding: '20px', fontSize: '13px', color: 'var(--color-text-muted)' }}>Nenhum dado disponível</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Empresa</th>
                  <th>Plano</th>
                  <th style={{ textAlign: 'right' }}>Instâncias</th>
                  <th style={{ textAlign: 'right' }}>Disparos usados</th>
                  <th style={{ textAlign: 'right' }}>Limite</th>
                  <th style={{ textAlign: 'right' }}>Créditos disparos</th>
                  <th style={{ textAlign: 'right' }}>IA usado</th>
                  <th style={{ textAlign: 'right' }}>Limite IA</th>
                  <th style={{ textAlign: 'right' }}>Créditos IA</th>
                </tr>
              </thead>
              <tbody>
                {Object.values(saldos).map(s => {
                  const dispPct = s.limite_disparos > 0 ? Math.round(s.disparos_usados / s.limite_disparos * 100) : 0;
                  const iaPct   = s.limite_ia > 0       ? Math.round(s.ia_usado / s.limite_ia * 100)             : 0;
                  const dispColor = dispPct >= 90 ? 'var(--color-danger)' : dispPct >= 70 ? 'var(--color-warning)' : 'var(--color-success)';
                  const iaColor   = iaPct   >= 90 ? 'var(--color-danger)' : iaPct   >= 70 ? 'var(--color-warning)' : 'var(--color-success)';
                  const instUsadas = s.instancias_usadas ?? 0;
                  const instLimite = s.limite_instancias ?? 0;
                  const instColor = instLimite > 0 && instUsadas >= instLimite ? 'var(--color-danger)' : 'var(--color-text)';
                  return (
                    <tr key={s.id}>
                      <td style={{ fontWeight: 500, fontSize: '13px' }}>{s.nome_empresa}</td>
                      <td><span className="badge" style={{ background: (planoBadge[s.plano] || {}).bg, color: (planoBadge[s.plano] || {}).color }}>{s.plano}</span></td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: instColor, fontSize: '13px' }}>
                        {instUsadas.toLocaleString('pt-BR')}<span style={{ color: 'var(--color-text-muted)', fontWeight: 400 }}> / {instLimite.toLocaleString('pt-BR')}</span>
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: dispColor, fontSize: '13px' }}>{s.disparos_usados.toLocaleString('pt-BR')}</td>
                      <td style={{ textAlign: 'right', fontSize: '13px', color: 'var(--color-text-muted)' }}>{s.limite_disparos.toLocaleString('pt-BR')}</td>
                      <td style={{ textAlign: 'right', fontSize: '13px' }}>
                        {s.disparos_creditos > 0
                          ? <span style={{ background: '#f0fdf4', color: '#15803d', borderRadius: '4px', padding: '2px 8px', fontWeight: 600 }}>+{s.disparos_creditos.toLocaleString('pt-BR')}</span>
                          : <span style={{ color: 'var(--color-text-muted)' }}>—</span>}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: iaColor, fontSize: '13px' }}>{s.ia_usado.toLocaleString('pt-BR')}</td>
                      <td style={{ textAlign: 'right', fontSize: '13px', color: 'var(--color-text-muted)' }}>{s.limite_ia.toLocaleString('pt-BR')}</td>
                      <td style={{ textAlign: 'right', fontSize: '13px' }}>
                        {s.ia_creditos > 0
                          ? <span style={{ background: '#f0fdf4', color: '#15803d', borderRadius: '4px', padding: '2px 8px', fontWeight: 600 }}>+{s.ia_creditos.toLocaleString('pt-BR')}</span>
                          : <span style={{ color: 'var(--color-text-muted)' }}>—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ─── Blacklist global ──────────────────────────────────────── */}
      <div className="card" style={{ marginTop: '2rem' }}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '0 0 1rem', fontSize: '14px', fontWeight: 600 }}>
          <ShieldAlert size={16} color="var(--color-danger)" />
          {t('configuracoes.sec_blacklist')}
        </h3>
        {(() => {
          const instancias = [...new Set(blacklist.map(c => c.instancia).filter(Boolean))];
          const lista = filtroInstanciaBlacklist
            ? blacklist.filter(c => c.instancia === filtroInstanciaBlacklist)
            : blacklist;
          return (
            <>
              {instancias.length > 0 && (
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px', alignItems: 'center' }}>
                  <Filter size={13} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
                  <button onClick={() => setFiltroInstanciaBlacklist('')}
                    className={`pill${!filtroInstanciaBlacklist ? ' is-active' : ''}`}
                    style={!filtroInstanciaBlacklist ? { background: 'var(--color-primary)', borderColor: 'var(--color-primary)', color: '#fff' } : undefined}>
                    {t('configuracoes.blacklist_todas', { n: blacklist.length })}
                  </button>
                  {instancias.map(inst => (
                    <button key={inst}
                      onClick={() => setFiltroInstanciaBlacklist(prev => prev === inst ? '' : inst)}
                      className={`pill${filtroInstanciaBlacklist === inst ? ' is-active' : ''}`}
                      style={filtroInstanciaBlacklist === inst ? { background: '#13305f', borderColor: '#13305f', color: '#fff' } : { borderColor: '#86efac', color: '#13305f' }}>
                      📲 {inst} ({blacklist.filter(c => c.instancia === inst).length})
                    </button>
                  ))}
                </div>
              )}
              {lista.length === 0 ? (
                <div className="info-box info-box-success" style={{ fontSize: 13 }}>
                  {filtroInstanciaBlacklist
                    ? t('configuracoes.blacklist_vazio_instancia', { instancia: filtroInstanciaBlacklist })
                    : t('configuracoes.blacklist_vazio')}
                </div>
              ) : (
                <div style={{ maxHeight: '260px', overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)' }}>
                  {lista.map(c => (
                    <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 14px', borderBottom: '1px solid var(--color-divider)', background: 'var(--color-surface)' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '13px', fontWeight: 500 }}>
                          {c.nome || c.telefone}
                          {c.nome_empresa && <span style={{ fontSize: '11px', color: 'var(--color-text-muted)', marginLeft: '6px' }}>— {c.nome_empresa}</span>}
                        </div>
                        <div className="muted" style={{ fontSize: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <span>{c.telefone}</span>
                          {c.instancia && (
                            <span style={{ fontSize: '10px', background: '#dcfce7', color: '#13305f', borderRadius: '3px', padding: '1px 5px' }}>{c.instancia}</span>
                          )}
                          <span>{t('configuracoes.blacklist_bloqueado_em', { data: new Date(c.updated_at).toLocaleDateString('pt-BR') })}</span>
                        </div>
                      </div>
                      <button onClick={() => handleRemoverBlacklist(c.id)} disabled={removendoBlacklist === c.id}
                        className="btn btn-icon btn-sm btn-secondary">
                        <X size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          );
        })()}
      </div>

      {/* ─── Reconciliar instâncias órfãs ─────────────────────── */}
      <div className="card" style={{ marginTop: '1rem' }}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '0 0 0.5rem', fontSize: '14px', fontWeight: 600 }}>
          🔄 Reconciliar Instâncias Órfãs
        </h3>
        <p style={{ fontSize: 13, color: 'var(--color-muted)', margin: '0 0 0.75rem' }}>
          Remove do banco instâncias que existem no registro mas não estão mais na Evolution API. Resolve o bloqueio de tenants que não conseguem criar novas instâncias após migração.
        </p>
        <button onClick={handleReconciliarInstancias} disabled={reconciliando} className="btn btn-sm btn-primary">
          {reconciliando ? 'Verificando...' : '🔄 Reconciliar agora'}
        </button>
      </div>

      {/* ─── Warmup / Cap diário por instância ─────────────────────── */}
      {instanciasConfig.length > 0 && (
        <div className="card" style={{ marginTop: '1rem' }}>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '0 0 1rem', fontSize: '14px', fontWeight: 600 }}>
            <ShieldCheck size={16} color="var(--color-accent)" />
            {t('configuracoes.sec_cap')}
          </h3>
          <div className="info-box" style={{ marginBottom: '1rem', fontSize: 13 }}>
            💡 <strong>Como funciona:</strong> {ajuda.warmup?.texto}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  {[t('configuracoes.cap_col_instancia'), t('configuracoes.cap_col_empresa'), t('configuracoes.cap_col_dias'), t('configuracoes.cap_col_cap_auto'), t('configuracoes.cap_col_override'), t('configuracoes.cap_col_enviados'), ''].map(h => (
                    <th key={h} style={{ fontSize: '12px' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {instanciasConfig.map(inst => {
                  const chave = `${inst.nome}-${inst.tenant_id || 'na'}`;
                  const naoVinc = inst.nao_vinculada || !inst.tenant_id;
                  return (
                    <tr key={chave} style={naoVinc ? { background: '#fffbeb' } : undefined}>
                      <td style={{ fontWeight: 500, fontSize: 13 }}>
                        <span style={{ fontSize: '10px', background: '#dcfce7', color: '#13305f', borderRadius: '3px', padding: '2px 6px' }}>
                          📲 {inst.nome}
                        </span>
                      </td>
                      <td className="table-cell-muted" style={{ fontSize: 13 }}>
                        {naoVinc ? (
                          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                            <select
                              value={vincularTenant[inst.nome] || ''}
                              onChange={e => setVincularTenant(s => ({ ...s, [inst.nome]: e.target.value }))}
                              className="select"
                              style={{ fontSize: 12, padding: '4px 6px', minWidth: 140 }}
                            >
                              <option value="">— vincular a —</option>
                              {tenants.map(tn => (
                                <option key={tn.id} value={tn.id}>{tn.nome_empresa}</option>
                              ))}
                            </select>
                            <button
                              onClick={() => handleVincular(inst.nome)}
                              disabled={!vincularTenant[inst.nome] || vinculando === inst.nome}
                              className="btn btn-sm btn-primary"
                              style={{ fontSize: 11, padding: '4px 8px' }}
                            >
                              {vinculando === inst.nome ? '...' : 'Vincular'}
                            </button>
                          </div>
                        ) : inst.nome_empresa}
                      </td>
                      <td className="table-cell-muted" style={{ fontSize: 13 }}>
                        {naoVinc
                          ? '—'
                          : inst.dias_warmup < 7
                            ? <span style={{ color: 'var(--color-warning)', fontWeight: 600 }}>{t('configuracoes.cap_aquecendo', { dia: inst.dias_warmup })}</span>
                            : t('configuracoes.cap_dias', { n: inst.dias_warmup })}
                      </td>
                      <td style={{ fontSize: 13 }}>
                        {naoVinc ? '—' : (
                          <span className={`badge ${inst.dias_warmup < 7 ? 'badge-warning' : inst.dias_warmup < 21 ? 'badge-amber' : 'badge-success'}`}>
                            {t('configuracoes.cap_por_dia', { n: inst.cap_atual })}
                          </span>
                        )}
                      </td>
                      <td>
                        <input type="number" defaultValue={inst.cap_diario_override || ''} placeholder={t('configuracoes.cap_placeholder')}
                          min="1" disabled={naoVinc}
                          className="input input-sm" style={{ width: '90px' }} id={`cap-${chave}`} />
                      </td>
                      <td className="table-cell-muted" style={{ fontSize: 13 }}>
                        {naoVinc ? '—' : <><strong>{inst.enviados_hoje}</strong>/{inst.cap_diario_override || inst.cap_atual}</>}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button onClick={() => { const val = document.getElementById(`cap-${chave}`)?.value; handleSalvarCap(inst.nome, inst.tenant_id, val); }}
                            disabled={naoVinc || salvandoCap === chave} className="btn btn-sm btn-primary" style={{ fontSize: 12 }}
                            title={naoVinc ? 'Instância não vinculada a um tenant' : 'Salvar cap'}>
                            {salvandoCap === chave ? '...' : <Save size={12} />}
                          </button>
                          <button onClick={() => abrirDiagnosticoAgenda(inst.tenant_id, inst.nome, inst.nome_empresa)}
                            disabled={naoVinc} className="btn btn-sm" style={{ fontSize: 12 }}
                            title="Diagnosticar agenda da IA">
                            <Calendar size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─── Integrações restritas (Avec, futuras) ─────────────────── */}
      <div className="card" style={{ marginTop: '1rem' }}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '0 0 1rem', fontSize: '14px', fontWeight: 600 }}>
          🔌 Integrações restritas — Acessos liberados
        </h3>
        <div className="info-box" style={{ marginBottom: '1rem', fontSize: 13 }}>
          💡 Libere integrações específicas (Avec Beauty, Simples Dental etc.) por instância. Google Calendar e iCloud ficam abertos para todos.<br />
          <span style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>Esta lista indica <strong>autorizações de acesso</strong> — não significa que a integração está ativa. Para ver o status real, consulte Configurar IA de cada instância.</span>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, alignItems: 'flex-end' }}>
          <div style={{ flex: 1, minWidth: 180 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-secondary)' }}>Empresa</label>
            <select
              value={novaIntegracao.tenant_id}
              onChange={e => setNovaIntegracao(s => ({ ...s, tenant_id: e.target.value, instancia: '' }))}
              className="select"
              style={{ width: '100%', fontSize: 12 }}
            >
              <option value="">— selecionar —</option>
              {(dashboard?.tenants || []).map(tn => (
                <option key={tn.id} value={tn.id}>{tn.nome_empresa}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1, minWidth: 160 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-secondary)' }}>Instância</label>
            <input
              type="text"
              value={novaIntegracao.instancia}
              onChange={e => setNovaIntegracao(s => ({ ...s, instancia: e.target.value }))}
              placeholder="nome-da-instancia"
              className="input"
              style={{ width: '100%', fontSize: 12 }}
            />
          </div>
          <div style={{ minWidth: 140 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-secondary)' }}>Integração</label>
            <select
              value={novaIntegracao.integracao}
              onChange={e => setNovaIntegracao(s => ({ ...s, integracao: e.target.value }))}
              className="select"
              style={{ width: '100%', fontSize: 12 }}
            >
              <option value="avec">Avec Beauty</option>
            </select>
          </div>
          <button onClick={liberarIntegracao} disabled={salvandoIntegracao}
            className="btn btn-primary btn-sm"
            style={{ height: 32 }}>
            {salvandoIntegracao ? 'Liberando...' : 'Liberar'}
          </button>
        </div>

        {integracoesAcessos.length === 0 ? (
          <p style={{ fontSize: 12, color: 'var(--color-text-muted)', margin: 0 }}>
            Nenhum acesso liberado ainda.
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  {['Empresa', 'Instância', 'Integração autorizada', 'Liberado em', 'Origem', ''].map(h => (
                    <th key={h} style={{ fontSize: 12 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {integracoesAcessos.map(a => (
                  <tr key={`${a.tenant_id}-${a.instancia}-${a.integracao}`}>
                    <td style={{ fontSize: 13 }}>{a.nome_empresa || a.tenant_id.slice(0, 8)}</td>
                    <td style={{ fontSize: 13 }}>{a.instancia}</td>
                    <td style={{ fontSize: 13 }}>
                      <span style={{ background: '#dcfce7', color: '#13305f', borderRadius: 3, padding: '2px 6px', fontSize: 11 }}>
                        {a.integracao}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                      {new Date(a.liberado_em).toLocaleDateString('pt-BR')}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{a.liberado_por || '—'}</td>
                    <td>
                      <button onClick={() => revogarIntegracao(a.tenant_id, a.instancia, a.integracao)}
                        className="btn btn-danger btn-sm"
                        style={{ fontSize: 11, padding: '3px 8px' }}>
                        Revogar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {diagAgenda && (
        <div onClick={() => setDiagAgenda(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, padding: 20, maxWidth: 640, width: '100%', maxHeight: '85vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Calendar size={16} /> Diagnóstico de agenda
              </h3>
              <button onClick={() => setDiagAgenda(null)} className="btn btn-sm" style={{ padding: 4 }}>
                <X size={14} />
              </button>
            </div>
            <div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 12 }}>
              <strong>{diagAgenda.nome_empresa || diagAgenda.tenant_id?.slice(0, 8)}</strong> · 📲 {diagAgenda.instancia}
            </div>

            {diagAgenda.loading && <div style={{ padding: 20, textAlign: 'center' }}>Carregando…</div>}

            {!diagAgenda.loading && diagAgenda.resultado?.erro && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: 12, color: '#991b1b', fontSize: 13 }}>
                Erro: {diagAgenda.resultado.erro}
              </div>
            )}

            {!diagAgenda.loading && diagAgenda.resultado && !diagAgenda.resultado.erro && (
              <>
                <div style={{
                  background: diagAgenda.resultado.ok ? '#f0fdf4' : '#fef2f2',
                  border: `1px solid ${diagAgenda.resultado.ok ? '#bbf7d0' : '#fecaca'}`,
                  borderRadius: 6, padding: 12, marginBottom: 12, fontSize: 13
                }}>
                  <div style={{ fontWeight: 600, color: diagAgenda.resultado.ok ? '#15803d' : '#991b1b' }}>
                    {diagAgenda.resultado.ok ? '✅ Agenda operacional' : `❌ ${diagAgenda.resultado.motivo}`}
                  </div>
                  {diagAgenda.resultado.detalhes && (
                    <div style={{ marginTop: 4, color: 'var(--color-text-soft)' }}>{diagAgenda.resultado.detalhes}</div>
                  )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12, marginBottom: 12 }}>
                  <div><strong>Provedor:</strong> {diagAgenda.resultado.provedor || '—'}</div>
                  <div><strong>Ativo:</strong> {diagAgenda.resultado.ativo ? 'sim' : 'não'}</div>
                  <div><strong>Timezone:</strong> {diagAgenda.resultado.timezone}</div>
                  <div><strong>Duração:</strong> {diagAgenda.resultado.duracao_minutos} min</div>
                  <div><strong>Horário:</strong> {diagAgenda.resultado.hora_inicio} – {diagAgenda.resultado.hora_fim}</div>
                  <div><strong>Dias:</strong> {(diagAgenda.resultado.dias_semana || []).join(', ')}</div>
                  {diagAgenda.resultado.provedor === 'google' && (
                    <>
                      <div><strong>Google conectado:</strong> {diagAgenda.resultado.google_conectado ? 'sim' : 'não'}</div>
                      <div><strong>Calendar ID:</strong> {diagAgenda.resultado.google_calendar_id}</div>
                    </>
                  )}
                  {diagAgenda.resultado.provedor === 'icloud' && (
                    <div style={{ gridColumn: 'span 2' }}><strong>iCloud:</strong> {diagAgenda.resultado.icloud_conectado ? 'conectado' : 'não conectado'}</div>
                  )}
                  {diagAgenda.resultado.provedor === 'avec' && (
                    <div><strong>Avec:</strong> {diagAgenda.resultado.avec_conectado ? 'conectado' : 'não conectado'}</div>
                  )}
                  {typeof diagAgenda.resultado.slots_total === 'number' && (
                    <div><strong>Slots livres (14d):</strong> {diagAgenda.resultado.slots_total}</div>
                  )}
                </div>

                {diagAgenda.resultado.slots_amostra?.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Próximos slots:</div>
                    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: 'var(--color-text-soft)' }}>
                      {diagAgenda.resultado.slots_amostra.map((s, i) => (
                        <li key={i}>{s.exibicao}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {diagAgenda.resultado.ultimos_agendamentos?.length > 0 && (
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Últimos agendamentos da IA:</div>
                    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: 'var(--color-text-soft)' }}>
                      {diagAgenda.resultado.ultimos_agendamentos.map(a => (
                        <li key={a.id}>
                          {new Date(a.data_hora).toLocaleString('pt-BR')} — {a.nome_contato || a.telefone}
                          {a.assunto && ` · ${a.assunto}`}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {diagAgenda.resultado.ok && (
                  <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--color-border)' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
                      Criar evento retroativo (a IA confirmou no chat mas não registrou):
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 12 }}>
                      <input className="input" style={{ fontSize: 12, padding: '6px 8px' }} placeholder="Telefone (com DDI)"
                        value={criarEventoForm.telefone} onChange={e => setCriarEventoForm(f => ({ ...f, telefone: e.target.value }))} />
                      <input className="input" style={{ fontSize: 12, padding: '6px 8px' }} placeholder="Nome (opcional)"
                        value={criarEventoForm.nome} onChange={e => setCriarEventoForm(f => ({ ...f, nome: e.target.value }))} />
                      <input className="input" type="date" style={{ fontSize: 12, padding: '6px 8px' }}
                        value={criarEventoForm.data} onChange={e => setCriarEventoForm(f => ({ ...f, data: e.target.value }))} />
                      <input className="input" type="time" style={{ fontSize: 12, padding: '6px 8px' }}
                        value={criarEventoForm.hora} onChange={e => setCriarEventoForm(f => ({ ...f, hora: e.target.value }))} />
                      <input className="input" style={{ fontSize: 12, padding: '6px 8px', gridColumn: 'span 2' }} placeholder="Assunto (ex: profilaxia)"
                        value={criarEventoForm.assunto} onChange={e => setCriarEventoForm(f => ({ ...f, assunto: e.target.value }))} />
                    </div>
                    <button onClick={criarEventoRetroativo} disabled={criandoEvento}
                      className="btn btn-primary" style={{ marginTop: 8, fontSize: 12, width: '100%' }}>
                      {criandoEvento ? 'Criando…' : 'Criar evento + enviar confirmação WhatsApp'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
